/**
 * Copyright Mántum All Rights Reserved.
 */
import { forwardRef, useEffect, useRef, useState } from 'react';
import { KEY_TRACE_BASCULE, KEY_TRACE } from './trace';
import { normalize } from './frozen';
import { useGroup, ACTION_VALUE } from '../../group';
import { validator } from '@mantum/kanela';
import debug from 'debug';
import Frozen from './frozen';
import Internal from '../internal/input';
import Reveal from './reveal';

import type { Limit, Type } from '@mantum/kanela';

const logger = debug('kanela');
const render = debug('kanela:render');

export type PropTypes = {
    autocomplete?:
        | 'off'
        | 'email'
        | 'current-password'
        | 'username'
        | 'new-password';
    alert?: 'success' | 'error' | null;
    className?: string;
    disabled?: boolean;
    hidden?: boolean;
    limit?: Limit;
    mode?: 'display' | 'normal';
    error?: boolean;
    frozen?: boolean;
    max?: number;
    message?: string;
    min?: number;
    name?: string;
    omitValidation?: boolean;
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
    onChange?: (name?: string, value?: unknown) => void;
    onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
    onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    optional?: boolean;
    placeholder?: string;
    report?: boolean;
    reveal?: boolean;
    resize?: boolean;
    testid?: string;
    mutate?: boolean;
    type: Type;
    value?: string | number;
};

export default forwardRef<HTMLInputElement, PropTypes>(
    function Component(props, ref) {
        const {
            alert = null,
            disabled = false,
            mode = 'normal',
            frozen = false,
            name,
            report = false,
            resize = false,
            mutate = false,
            reveal = false,
            value = ''
        } = props;

        const groups = useGroup();
        const changeRef = useRef(props.onChange);

        const [hide, setHide] = useState(true);
        const [currentValue, setCurrentValue] = useState<string | number>('');
        const [trace, setTrace] = useState<string | undefined>('');

        useEffect(() => {
            changeRef.current = props.onChange;
        }, [props.onChange]);

        useEffect(() => {
            if (!frozen && changeRef.current && report) {
                changeRef.current(`${name}-manual`, false);
            }
        }, [name, frozen, report]);

        useEffect(() => {
            const current = !validator.isNull(value) ? value : '';
            const values =
                typeof current === 'number'
                    ? normalize(current, {
                          min: props.min,
                          max: props.max
                      })
                    : current;

            setCurrentValue(values);
        }, [value, props.min, props.max]);

        useEffect(() => {
            if (!frozen && changeRef.current) {
                changeRef.current(
                    name,
                    !validator.isNull(value) && typeof value === 'number'
                        ? normalize(value, {
                              min: props.min,
                              max: props.max
                          })
                        : value
                );
            }
        }, [frozen, name, value, props.min, props.max]);

        useEffect(() => {
            if (!name || !groups) {
                return;
            }

            if (!(name in groups)) {
                return;
            }

            const content = groups[name];
            if (content.action === ACTION_VALUE) {
                const current =
                    typeof content.value === 'string' ||
                    typeof content.value === 'number'
                        ? content.value
                        : '';

                const value =
                    !validator.isNull(current) && typeof current === 'number'
                        ? normalize(current, {
                              min: props.min,
                              max: props.max
                          })
                        : current;

                setCurrentValue(value);

                logger('receive dispatch change value "%s": %s', name, value);
                if (!frozen && changeRef.current) {
                    changeRef.current(name, value);
                    if (name && report) {
                        changeRef.current(`${name}-manual`, false);
                    }
                }
            }
        }, [frozen, name, groups, props.min, props.max, report]);

        useEffect(() => {
            if (!KEY_TRACE_BASCULE || !groups) {
                return;
            }

            if (!(KEY_TRACE_BASCULE in groups)) {
                return;
            }

            const contentTrace = groups[KEY_TRACE_BASCULE];
            if (contentTrace.action === ACTION_VALUE) {
                const current =
                    contentTrace.value !== undefined &&
                    typeof contentTrace.value === 'string'
                        ? contentTrace.value
                        : '';

                setTrace(current);
                logger(
                    'receive dispatch change value "%s": %s',
                    `${name}-${KEY_TRACE}`,
                    current
                );
            }
        }, [groups, name, frozen]);

        const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
            if (props.onKeyDown) {
                props.onKeyDown(event);
            }

            if (!frozen && changeRef.current) {
                if (name && report) {
                    changeRef.current(`${name}-manual`, true);
                }
            }
        };

        const onChange = (name?: string, value?: unknown) => {
            let current = value as string | number;
            setCurrentValue(current);

            if (!frozen && changeRef.current) {
                if (props.type === 'number') {
                    current = normalize(Number(value), {
                        min: props.min,
                        max: props.max
                    });
                }
                changeRef.current(name, current);
            }
        };

        const onBlur = (event: React.FocusEvent<HTMLInputElement>) => {
            const value = event.target.value;
            if (value && props.type === 'number') {
                setCurrentValue(
                    normalize(Number(value), {
                        min: props.min,
                        max: props.max
                    })
                );
            }

            if (props.onBlur) {
                props.onBlur(event);
            }
        };

        const onHide = () => {
            setHide((previous) => !previous);
        };

        const className = reveal ? 'mr-[39px] rounded-e-none' : '';

        render('[Input]: %o', {
            value,
            current: currentValue
        });

        return (
            <>
                <Internal
                    alert={alert}
                    autocomplete={props.autocomplete}
                    className={className}
                    disabled={disabled}
                    error={props.error}
                    limit={props.limit}
                    max={props.max}
                    min={props.min}
                    mode={mode}
                    mutate={mutate}
                    name={!frozen ? name : `${name}-system`}
                    onBlur={onBlur}
                    onChange={onChange}
                    onFocus={props.onFocus}
                    onKeyDown={onKeyDown}
                    placeholder={props.placeholder}
                    ref={ref}
                    resize={resize}
                    reveal={props.reveal}
                    testid={props.testid}
                    type={hide ? props.type : 'text'}
                    value={currentValue}
                />
                {frozen && (
                    <Frozen
                        alert={alert}
                        max={props.max}
                        min={props.min}
                        name={name}
                        mode={mode}
                        onChange={changeRef.current}
                        resize={mutate}
                        mutate={mutate}
                        trace={trace}
                        hidden={props.hidden}
                        value={currentValue}
                    />
                )}
                {reveal && <Reveal visible={hide} onClick={onHide} />}
            </>
        );
    }
);
